/**
 * URL Builder Utility
 *
 * Builds URLs with company and installation context for bookmarkable navigation.
 * All authenticated routes follow the pattern:
 * - Company level: /app/company/:companyId/...
 * - Installation level: /app/company/:companyId/installation/:installationId/...
 */

// Company-level paths (no installation required)
export const buildCompanyPath = {
    about: (companyId) => `/app/company/${companyId}/about`,
    managedCompanies: (companyId) => `/app/company/${companyId}/managed-companies`,
    teamMembers: (companyId) => `/app/company/${companyId}/team-members`,
    alerts: (companyId) => `/app/company/${companyId}/alerts`,
    activityLog: (companyId) => `/app/company/${companyId}/activity-log`,
    newInstallation: (companyId) => `/app/company/${companyId}/installation/new`,

    // System settings (company level)
    territories: (companyId) => `/app/company/${companyId}/system-settings/territories`,
    statutoryHolidays: (companyId) => `/app/company/${companyId}/system-settings/statutory-holidays`,
    timeOfUsePlans: (companyId) => `/app/company/${companyId}/system-settings/time-of-use-plans`,
    salesTaxPlans: (companyId) => `/app/company/${companyId}/system-settings/sales-tax-plans`,
};

// Installation-level paths (require both company and installation)
export const buildInstallationPath = {
    settings: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/settings`,
    chargepointStatus: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/chargepoint-status`,
    siteLoad: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/site-load`,
    drivers: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/drivers`,
    driverDetail: (companyId, installationId, driverId) =>
        `/app/company/${companyId}/installation/${installationId}/drivers/${driverId}`,
    driverChargepoints: (companyId, installationId, driverId) =>
        `/app/company/${companyId}/installation/${installationId}/drivers/${driverId}/chargepoints`,
    invoices: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/invoices`,
    transactions: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/transactions`,
    billingSettings: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/billing-settings`,
    controllers: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/controllers`,
    controllerDetail: (companyId, installationId, controllerId) =>
        `/app/company/${companyId}/installation/${installationId}/controllers/${controllerId}`,
    controllerSensing: (companyId, installationId, controllerId) =>
        `/app/company/${companyId}/installation/${installationId}/controllers/${controllerId}/sensing`,
    controllerTopology: (companyId, installationId, controllerId) =>
        `/app/company/${companyId}/installation/${installationId}/controllers/${controllerId}/topology`,
    chargepoints: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/chargepoints`,
    licences: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/licences`,
    projectManagement: (companyId, installationId) =>
        `/app/company/${companyId}/installation/${installationId}/project-management`,
};

// Route patterns (for matching current path to menu items)
export const PATH_PATTERNS = {
    // Company level
    ABOUT: '/app/company/:companyId/about',
    MANAGED_COMPANIES: '/app/company/:companyId/managed-companies',
    TEAM_MEMBERS: '/app/company/:companyId/team-members',
    ALERTS: '/app/company/:companyId/alerts',
    ACTIVITY_LOG: '/app/company/:companyId/activity-log',
    SEARCH: '/app/company/:companyId/search',
    NEW_INSTALLATION: '/app/company/:companyId/installation/new',

    // System settings
    SYSTEM_SETTING_TERRITORIES: '/app/company/:companyId/system-settings/territories',
    SYSTEM_SETTING_HOLIDAYS: '/app/company/:companyId/system-settings/statutory-holidays',
    SYSTEM_SETTING_TIME_OF_USE: '/app/company/:companyId/system-settings/time-of-use-plans',
    SYSTEM_SETTING_SALES_TAX: '/app/company/:companyId/system-settings/sales-tax-plans',

    // Installation level
    INSTALLATION_SETTINGS: '/app/company/:companyId/installation/:installationId/settings',
    INSTALLATION_CHARGEPOINT_STATUS: '/app/company/:companyId/installation/:installationId/chargepoint-status',
    INSTALLATION_SITE_LOAD: '/app/company/:companyId/installation/:installationId/site-load',
    INSTALLATION_DRIVERS: '/app/company/:companyId/installation/:installationId/drivers',
    INSTALLATION_DRIVER_DETAIL: '/app/company/:companyId/installation/:installationId/drivers/:driverId',
    INSTALLATION_DRIVER_CHARGEPOINTS: '/app/company/:companyId/installation/:installationId/drivers/:driverId/chargepoints',
    INSTALLATION_INVOICES: '/app/company/:companyId/installation/:installationId/invoices',
    INSTALLATION_TRANSACTIONS: '/app/company/:companyId/installation/:installationId/transactions',
    INSTALLATION_BILLING_SETTINGS: '/app/company/:companyId/installation/:installationId/billing-settings',
    INSTALLATION_CONTROLLERS: '/app/company/:companyId/installation/:installationId/controllers',
    INSTALLATION_CONTROLLER_DETAIL: '/app/company/:companyId/installation/:installationId/controllers/:controllerId',
    INSTALLATION_CONTROLLER_SENSING: '/app/company/:companyId/installation/:installationId/controllers/:controllerId/sensing',
    INSTALLATION_CONTROLLER_TOPOLOGY: '/app/company/:companyId/installation/:installationId/controllers/:controllerId/topology',
    INSTALLATION_CHARGEPOINTS: '/app/company/:companyId/installation/:installationId/chargepoints',
    INSTALLATION_LICENCES: '/app/company/:companyId/installation/:installationId/licences',
    INSTALLATION_PROJECT_MANAGEMENT: '/app/company/:companyId/installation/:installationId/project-management',
};

/**
 * Convert a pattern to an actual path by replacing placeholders
 */
export const buildPathFromPattern = (pattern, companyId, installationId = null) => {
    let path = pattern.replace(':companyId', companyId);
    if (installationId) {
        path = path.replace(':installationId', installationId);
    }
    return path;
};

/**
 * Extract company and installation IDs from a URL path.
 * Supports standard `/app/company/:id/...` and legacy/alternate segments such as
 * `/app/user-group/:id/installation/:installationId/...`.
 */
export const parseContextFromPath = (pathname) => {
    if (!pathname) {
        return { companyId: null, installationId: null };
    }
    let companyId = null;
    const companyStandard = pathname.match(/\/app\/company\/(\d+)/);
    const companyAlt = pathname.match(/\/app\/(?:user-group|userGroup|user-portal|userPortal)\/(\d+)/i);
    if (companyStandard) {
        companyId = companyStandard[1];
    } else if (companyAlt) {
        companyId = companyAlt[1];
    }
    const installationMatch = pathname.match(/\/installation\/(\d+)/);
    return {
        companyId,
        installationId: installationMatch ? installationMatch[1] : null,
    };
};

export function parseInstallationIdFromPath(pathname) {
    return parseContextFromPath(pathname).installationId;
}

/** sessionStorage key: last visited installation within a company (for company-level routes like Search). */
export const lastInstallationStorageKey = (companyId) =>
    companyId ? `vg-ui.lastInstallationId.${companyId}` : null;

/**
 * Escape special regex characters in a string
 */
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Check if a pathname matches a route pattern
 */
export const pathMatchesPattern = (pathname, pattern) => {
    // Split by route parameters, escape static parts, then rejoin
    const parts = pattern.split(/:[a-zA-Z]+/);
    const escapedParts = parts.map(escapeRegex);
    const regexPattern = escapedParts.join('\\d+');

    const regex = new RegExp('^' + regexPattern + '$');
    return regex.test(pathname);
};

/**
 * Check if pathname matches any of the patterns in an array
 */
export const pathMatchesAnyPattern = (pathname, patterns) => {
    return patterns.some(pattern => pathMatchesPattern(pathname, pattern));
};
